import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { styles as S } from '../styles';
import { useDialog } from '../useDialog';
import { TAGE_LANG, TYP_LABEL } from '../utils';
import { api, KonfliktError, SperreError, OeffnungError } from '../api';
import { IconRepeat, IconWarn, IconX } from '../icons';
const LEER = {
    platz_id: 0, team_id: 0, titel: '', typ: 'training', wochentag: 1,
    start_zeit: '18:00', end_zeit: '19:30', wiederkehrend: true,
    datum: null, saison_start: null, saison_ende: null, notiz: null,
};
const STATUS_LABEL = {
    bestaetigt: 'Bestätigt',
    angefragt: 'Angefragt',
    abgelehnt: 'Abgelehnt',
};
export function BelegungModal({ belegung, plaetze, teams, teamById, onClose, onSaved, settings, occurrence, canManage = true, meineTeams = [], aktiveSaison = null, }) {
    const dialogRef = useDialog(onClose);
    const istNeu = belegung === null;
    const istSerie = !!belegung?.wiederkehrend;
    const istTrainer = !canManage;
    const status = belegung?.status ?? 'bestaetigt';
    // Trainer dürfen nur eigene, noch offene Anfragen bearbeiten
    const nurLesen = istTrainer && !istNeu && status !== 'angefragt';
    // Auswählbare Mannschaften (Trainer: nur eigene)
    const teamAuswahl = istTrainer ? teams.filter((t) => meineTeams.includes(t.id)) : teams;
    // Bei Klick auf ein Serien-Vorkommen startet der Scope auf "termin",
    // sonst auf "serie".
    const [scope, setScope] = useState(occurrence ? 'termin' : 'serie');
    const [form, setForm] = useState(belegung
        ? { ...belegung }
        : {
            ...LEER,
            platz_id: plaetze[0]?.id ?? 0,
            team_id: istTrainer ? (teamAuswahl[0]?.id ?? 0) : 0,
            // Neue Serien mit der aktiven Saison (sonst Settings) vorbelegen
            saison_start: aktiveSaison?.start || settings?.saison_start || null,
            saison_ende: aktiveSaison?.ende || settings?.saison_ende || null,
        });
    const [speichert, setSpeichert] = useState(false);
    const [konflikte, setKonflikte] = useState(null);
    const [gesperrt, setGesperrt] = useState(null);
    const [fehler, setFehler] = useState(null);
    const setF = (key, wert) => setForm((f) => ({ ...f, [key]: wert }));
    const einzelTermin = istSerie && scope === 'termin' && occurrence;
    /** Status setzen (Freigeben/Ablehnen) – nur Vorstand/Admin. */
    async function statusSetzen(neu) {
        if (!belegung) {
            return;
        }
        setSpeichert(true);
        try {
            await api.setBelegungStatus(belegung.id, neu);
            onSaved();
            onClose();
        }
        catch (e) {
            setFehler(e instanceof Error ? e.message : 'Aktion fehlgeschlagen');
            setSpeichert(false);
        }
    }
    async function speichern(force = false) {
        setSpeichert(true);
        setFehler(null);
        setGesperrt(null);
        try {
            if (einzelTermin) {
                // Nur diesen Termin: als Ausnahme 'geaendert' speichern
                await api.saveAusnahme({
                    belegung_id: belegung.id,
                    datum: occurrence.datum,
                    status: 'geaendert',
                    platz_id: form.platz_id,
                    team_id: form.team_id,
                    titel: form.titel || null,
                    typ: form.typ,
                    start_zeit: form.start_zeit,
                    end_zeit: form.end_zeit,
                    notiz: form.notiz,
                });
            }
            else if (istNeu) {
                await api.createBelegung(form, force);
            }
            else {
                await api.updateBelegung(belegung.id, form, force);
            }
            onSaved();
            onClose();
        }
        catch (e) {
            if (e instanceof KonfliktError) {
                setKonflikte(e.konflikte);
            }
            else if (e instanceof SperreError) {
                setGesperrt(`Platz gesperrt – ${e.sperre.grund || 'Wartung/Sperrung'}`);
            }
            else if (e instanceof OeffnungError) {
                setGesperrt(`Außerhalb der Öffnungszeit (${e.von}–${e.bis})`);
            }
            else {
                setFehler(e instanceof Error ? e.message : 'Unbekannter Fehler');
            }
        }
        finally {
            setSpeichert(false);
        }
    }
    /** „Dieser Termin fällt aus" – Ausnahme 'entfaellt'. */
    async function terminEntfaellt() {
        if (!belegung || !occurrence) {
            return;
        }
        setSpeichert(true);
        try {
            await api.saveAusnahme({
                belegung_id: belegung.id,
                datum: occurrence.datum,
                status: 'entfaellt',
            });
            onSaved();
            onClose();
        }
        catch (e) {
            setFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
            setSpeichert(false);
        }
    }
    /** Ausnahme entfernen → Termin folgt wieder der Serie. */
    async function ausnahmeEntfernen() {
        if (!occurrence?.ausnahmeId) {
            return;
        }
        setSpeichert(true);
        try {
            await api.deleteAusnahme(occurrence.ausnahmeId);
            onSaved();
            onClose();
        }
        catch (e) {
            setFehler(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
            setSpeichert(false);
        }
    }
    async function loeschen() {
        if (istNeu) {
            return;
        }
        setSpeichert(true);
        try {
            await api.deleteBelegung(belegung.id);
            onSaved();
            onClose();
        }
        catch (e) {
            setFehler(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
            setSpeichert(false);
        }
    }
    return (_jsx("div", { style: S.overlay, onClick: onClose, children: _jsxs("div", { ref: dialogRef, role: "dialog", "aria-modal": true, "aria-label": "Belegung", style: S.modal, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { style: S.modalHead, children: [_jsx("h3", { style: S.modalTitel, children: istNeu
                                ? 'Neue Belegung'
                                : einzelTermin
                                    ? 'Termin bearbeiten'
                                    : 'Belegung bearbeiten' }), _jsx("button", { style: S.iconBtn, onClick: onClose, "aria-label": "Schlie\u00DFen", children: _jsx(IconX, { size: 18 }) })] }), _jsxs("div", { style: S.modalBody, children: [!istNeu && (_jsx("div", { style: {
                                ...S.statusBadge,
                                ...(status === 'angefragt' ? S.statusAngefragt
                                    : status === 'abgelehnt' ? S.statusAbgelehnt
                                        : S.statusBestaetigt),
                            }, children: STATUS_LABEL[status] })), istTrainer && istNeu && (_jsxs("div", { style: { ...S.modalKonflikt, background: '#fef9e7', borderColor: '#f5e6b8', color: '#8a6d1a' }, children: [_jsx(IconWarn, { size: 16 }), _jsxs("div", { children: ["Deine Eingabe wird als ", _jsx("strong", { children: "Anfrage" }), " gespeichert und muss vom Vorstand freigegeben werden."] })] })), nurLesen && (_jsxs("div", { style: { ...S.modalKonflikt, background: '#f4f5f3', borderColor: '#e4e7e2', color: '#5a6258' }, children: [_jsx(IconWarn, { size: 16 }), _jsx("div", { children: "Diese Belegung wurde bereits entschieden und kann von dir nicht mehr ge\u00E4ndert werden." })] })), istSerie && occurrence && (_jsxs("div", { style: S.scopeToggle, children: [_jsxs("button", { onClick: () => setScope('termin'), style: scope === 'termin' ? S.scopeActive : S.scopeBtn, children: ["Nur am ", formatDatum(occurrence.datum)] }), _jsx("button", { onClick: () => setScope('serie'), style: scope === 'serie' ? S.scopeActive : S.scopeBtn, children: "Ganze Serie" })] })), _jsx(Feld, { label: "Platz", children: _jsx("select", { style: S.input, value: form.platz_id, onChange: (e) => setF('platz_id', Number(e.target.value)), children: plaetze.map((p) => (_jsxs("option", { value: p.id, children: [p.name, " (", p.typ, ")"] }, p.id))) }) }), _jsx(Feld, { label: "Mannschaft / Gruppe", children: _jsxs("select", { style: S.input, value: form.team_id, disabled: nurLesen, onChange: (e) => setF('team_id', Number(e.target.value)), children: [!istTrainer && _jsx("option", { value: 0, children: "\u2014 keine \u2014" }), teamAuswahl.map((t) => (_jsx("option", { value: t.id, children: t.name }, t.id)))] }) }), _jsxs("div", { style: S.feldReihe, children: [_jsx(Feld, { label: einzelTermin ? 'Datum' : 'Wochentag', children: einzelTermin ? (_jsx("input", { style: { ...S.input, background: '#f4f5f3' }, value: formatDatum(occurrence.datum), disabled: true })) : (_jsx("select", { style: S.input, value: form.wochentag, onChange: (e) => setF('wochentag', Number(e.target.value)), children: TAGE_LANG.map((t, i) => (_jsx("option", { value: i + 1, children: t }, t))) })) }), _jsx(Feld, { label: "Art", children: _jsx("select", { style: S.input, value: form.typ, onChange: (e) => setF('typ', e.target.value), children: Object.entries(TYP_LABEL).map(([k, v]) => (_jsx("option", { value: k, children: v }, k))) }) })] }), _jsxs("div", { style: S.feldReihe, children: [_jsx(Feld, { label: "Von", children: _jsx("input", { type: "time", style: S.input, value: form.start_zeit, onChange: (e) => setF('start_zeit', e.target.value) }) }), _jsx(Feld, { label: "Bis", children: _jsx("input", { type: "time", style: S.input, value: form.end_zeit, onChange: (e) => setF('end_zeit', e.target.value) }) })] }), !einzelTermin && (_jsxs("label", { style: S.checkRow, children: [_jsx("input", { type: "checkbox", checked: form.wiederkehrend, onChange: (e) => setF('wiederkehrend', e.target.checked) }), _jsx(IconRepeat, { size: 14 }), " W\u00F6chentlich wiederholen (ganze Saison)"] })), !einzelTermin && (form.wiederkehrend ? (_jsxs("div", { style: S.feldReihe, children: [_jsx(Feld, { label: "Saison von", children: _jsx("input", { type: "date", style: S.input, value: form.saison_start ?? '', onChange: (e) => setF('saison_start', e.target.value || null) }) }), _jsx(Feld, { label: "Saison bis", children: _jsx("input", { type: "date", style: S.input, value: form.saison_ende ?? '', onChange: (e) => setF('saison_ende', e.target.value || null) }) })] })) : (_jsx(Feld, { label: "Datum", children: _jsx("input", { type: "date", style: S.input, value: form.datum ?? '', onChange: (e) => setF('datum', e.target.value || null) }) }))), konflikte && (_jsxs("div", { style: S.modalKonflikt, children: [_jsx(IconWarn, { size: 16 }), _jsxs("div", { children: [_jsx("strong", { children: "\u00DCberschneidung:" }), " Dieser Platz ist zur gew\u00E4hlten Zeit bereits belegt", konflikte[0] && (_jsxs(_Fragment, { children: [" \u2013 z.\u00A0B. ", teamById(konflikte[0].team_id)?.name ?? 'eine andere Belegung', "\u00A0(", konflikte[0].start_zeit, "\u2013", konflikte[0].end_zeit, ")"] })), "."] })] })), gesperrt && (_jsxs("div", { style: S.modalKonflikt, children: [_jsx(IconWarn, { size: 16 }), _jsxs("div", { children: [_jsx("strong", { children: "Hinweis:" }), " ", gesperrt, ". Du kannst die Belegung dennoch anlegen."] })] })), fehler && (_jsxs("div", { style: S.modalKonflikt, children: [_jsx(IconWarn, { size: 16 }), " ", fehler] }))] }), _jsxs("div", { style: S.modalFoot, children: [!istNeu && belegung.typ === 'training' && belegung.team_id > 0 && window.MPPlatzbelegung?.taUrl && (_jsx("a", { href: `${window.MPPlatzbelegung.taUrl}&pb_id=${belegung.id}`, target: "_blank", rel: "noreferrer", style: {
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: '#f0fdf4', color: '#2f7d32',
                                border: '1px solid #a7c9a7', borderRadius: 6,
                                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                                textDecoration: 'none', marginRight: 'auto',
                            }, children: "\uD83D\uDCCB Trainingsanwesenheit \u00F6ffnen" })), nurLesen ? (_jsx("button", { style: S.primaryBtn, onClick: onClose, children: "Schlie\u00DFen" })) : einzelTermin ? (_jsxs(_Fragment, { children: [occurrence.ausnahmeId ? (_jsx("button", { style: S.deleteBtn, onClick: ausnahmeEntfernen, disabled: speichert, children: "Ausnahme entfernen" })) : (_jsx("button", { style: S.deleteBtn, onClick: terminEntfaellt, disabled: speichert, children: "F\u00E4llt aus" })), _jsx("button", { style: S.primaryBtn, onClick: () => speichern(false), disabled: speichert, children: speichert ? 'Speichert …' : 'Nur diesen Termin speichern' })] })) : (_jsxs(_Fragment, { children: [!istNeu && (_jsx("button", { style: S.deleteBtn, onClick: loeschen, disabled: speichert, children: istTrainer ? 'Zurückziehen' : 'Löschen' })), canManage && !istNeu && status === 'angefragt' && (_jsxs(_Fragment, { children: [_jsx("button", { style: S.deleteBtn, onClick: () => statusSetzen('abgelehnt'), disabled: speichert, children: "Ablehnen" }), _jsx("button", { style: { ...S.primaryBtn, background: '#2f7d32' }, onClick: () => statusSetzen('bestaetigt'), disabled: speichert, children: "Freigeben" })] })), (konflikte || gesperrt) ? (_jsx("button", { style: S.forceBtn, onClick: () => speichern(true), disabled: speichert, children: istTrainer ? 'Trotzdem anfragen' : 'Trotzdem speichern' })) : (_jsx("button", { style: S.primaryBtn, onClick: () => speichern(false), disabled: speichert, children: speichert ? 'Speichert …' : istTrainer ? (istNeu ? 'Anfragen' : 'Anfrage aktualisieren') : 'Speichern' }))] }))] })] }) }));
}
function Feld({ label, children }) {
    return (_jsxs("div", { style: { flex: 1 }, children: [_jsx("label", { style: S.label, children: label }), children] }));
}
/** "2026-06-17" → "17.06.2026" */
function formatDatum(iso) {
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
}
